/**
 * 日ビューに重ねる「今日の過ごし方」の表示
 *
 * - 時間軸の左端の色帯: 時間帯ごとの調子（緑=集中できる / 青=ふつう / 黄=省エネ）
 * - TODO 列の背景: 集中できる時間・運動におすすめの時間（背景はクリックを通して空き枠の作成を妨げない）
 * - TODO 列の右端の帯: 同じ時間を色とアイコンで。タスクはこの帯を避けて置くので隠れない（押すと詳細を開く）
 * - TODO 列の破線ブロック: 今日やってみる TODO の提案（「この時間に入れる」で実際の予定にする）
 */

import { Brain, CalendarPlus, Dumbbell, Loader2, Sparkles } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import type { TodayAdviceResponse, TodayEnergy } from '@/api/types/sleep'
import type { TodoResponse } from '@/api/types/todo'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'
import { useSleepStore } from '@/store/useSleepStore'

import { formatJstDate, formatJstTime, formatMinutes } from '../sleep/format'
import { blockHorizontalStyle, type OverlapSlot } from './overlap-layout'
import { applySchedule } from './schedule-actions'
import { hasSlotLane, type PickOverlay, type SlotOverlay, type TodayOverlay } from './today-overlay'
import { SLOT_LANE_WIDTH, SLOT_STYLE } from './today-slot-style'
import { TodaySettingsButton } from './TodaySettingsButton'

const TIME_AXIS_WIDTH = 48
const ENERGY_BAND_WIDTH = 4
const MINUTE_MS = 60_000

const TODO_COLUMN_LEFT = `calc(${TIME_AXIS_WIDTH}px + (100% - ${TIME_AXIS_WIDTH}px) / 3)`
const TODO_COLUMN_WIDTH = `calc((100% - ${TIME_AXIS_WIDTH}px) / 3)`
const LANE_LEFT = `calc(${TODO_COLUMN_LEFT} + ${TODO_COLUMN_WIDTH} - ${SLOT_LANE_WIDTH}px)`

const ENERGY_STYLE = new Map<TodayEnergy, { label: string; className: string }>([
  ['high', { label: '集中できる', className: 'bg-emerald-500' }],
  ['medium', { label: 'ふつう', className: 'bg-sky-500' }],
  ['low', { label: '省エネで', className: 'bg-amber-400' }],
])

const CONDITION_STYLE = new Map<TodayAdviceResponse['condition']['level'], string>([
  ['good', 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'],
  ['normal', 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'],
  ['low', 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'],
])

const timeRange = (start: string, end: string) => `${formatJstTime(start)}〜${formatJstTime(end)}`

const useNow = () => {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), MINUTE_MS)
    return () => clearInterval(id)
  }, [])
  return now
}

type Geometry = { hourHeight: number }

const verticalStyle = ({ start, end }: { start: Date; end: Date }, { hourHeight }: Geometry) => {
  const startMin = start.getHours() * 60 + start.getMinutes()
  const endMin = end.getHours() * 60 + end.getMinutes() || 24 * 60
  return { top: (startMin / 60) * hourHeight, height: Math.max(((endMin - startMin) / 60) * hourHeight, 4) }
}

const SlotItem = ({ item, geometry }: { item: SlotOverlay; geometry: Geometry }) => {
  const style = SLOT_STYLE.get(item.slot.kind)
  const Icon = style?.icon ?? Brain
  const { top, height } = verticalStyle(item, geometry)
  return (
    <>
      <div
        className={cn('pointer-events-none absolute z-4 border-l-2', style?.tint)}
        style={{ top, height, left: TODO_COLUMN_LEFT, width: TODO_COLUMN_WIDTH }}
      />
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`${item.slot.title}の詳細`}
            title={`${item.slot.title} ${timeRange(item.slot.start, item.slot.end)}`}
            className={cn(
              'absolute z-9 flex justify-center rounded-sm pt-0.5 text-white opacity-85 hover:opacity-100',
              style?.tab,
            )}
            style={{
              top: top + 1,
              height: Math.max(height - 2, 4),
              left: `calc(${LANE_LEFT} + 2px)`,
              width: SLOT_LANE_WIDTH - 3,
            }}
          >
            {height >= 16 && <Icon className="size-2.5" />}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 space-y-1.5 p-3 text-sm">
          <p className={cn('flex items-center gap-1.5 font-semibold', style?.text)}>
            <Icon className="size-4" />
            {item.slot.title}
          </p>
          <p className="text-xs tabular-nums text-muted-foreground">
            {timeRange(item.slot.start, item.slot.end)}（{formatMinutes(item.slot.minutes)}）
          </p>
          <p className="text-xs">{item.slot.detail}</p>
          {item.todoTitles.length > 0 && (
            <p className={cn('text-xs wrap-break-word', style?.text)}>やること: {item.todoTitles.join(' / ')}</p>
          )}
        </PopoverContent>
      </Popover>
    </>
  )
}

/** 集中・運動の時間と重なったタスクの右上に付ける印 */
export const SlotBadge = ({ item }: { item: SlotOverlay }) => {
  const style = SLOT_STYLE.get(item.slot.kind)
  const Icon = style?.icon ?? Brain
  return (
    <span
      title={`${item.slot.title}（${timeRange(item.slot.start, item.slot.end)}）と重なっています`}
      className={cn(
        'absolute top-0.5 right-0.5 flex size-3.5 items-center justify-center rounded-full text-white ring-1 ring-white/80',
        style?.tab,
      )}
    >
      <Icon className="size-2.5" />
    </span>
  )
}

type PickProps = {
  item: PickOverlay
  geometry: Geometry
  now: number
  todos: TodoResponse[]
  onTodoClick?: (todo: TodoResponse) => void
  layout: Map<string, OverlapSlot>
  laneWidth: number
}

const PickItem = ({ item, geometry, now, todos, onTodoClick, layout, laneWidth }: PickProps) => {
  const markPickScheduled = useSleepStore((s) => s.markPickScheduled)
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const { pick } = item
  const start = pick.suggested_start as string
  const end = pick.suggested_end as string
  const past = item.start.getTime() < now
  const { top, height } = verticalStyle(item, geometry)
  const todo = todos.find((t) => t.id === pick.todo_id)

  const handleSchedule = async () => {
    setSaving(true)
    try {
      await applySchedule(pick.todo_id, parseUtcDate(start), parseUtcDate(end))
      markPickScheduled(pick.todo_id, start, end)
      toast.success(`「${pick.title}」を ${timeRange(start, end)} に入れました`)
      setOpen(false)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '予定に入れられませんでした')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'absolute z-8 overflow-hidden rounded border border-dashed border-indigo-500 bg-indigo-500/15 px-1.5 py-0.5 text-left text-xs text-indigo-800 hover:bg-indigo-500/25 dark:text-indigo-200',
            past && 'opacity-50',
          )}
          style={{
            top,
            height,
            // 既存のタスクと重なるときは、タスク同士と同じように左右に分けて並べる
            ...blockHorizontalStyle(1, laneWidth, layout.get(item.id) ?? { subCol: 0, totalCols: 1 }),
          }}
        >
          {height > 28 && (
            <div className="text-[10px] font-bold leading-tight tabular-nums">{timeRange(start, end)}</div>
          )}
          <div className="truncate font-medium leading-tight">
            <Sparkles className="mr-0.5 inline size-3" />
            {pick.title}
          </div>
          {height > 40 && <div className="truncate text-[10px] opacity-80">{pick.reason}</div>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-2 p-3 text-sm">
        <div className="space-y-0.5">
          <p className="flex items-center gap-1 text-[11px] text-indigo-600 dark:text-indigo-300">
            <Sparkles className="size-3" />
            今日やってみる TODO の提案
          </p>
          <p className="font-semibold wrap-break-word">{pick.title}</p>
          <p className="text-xs tabular-nums text-muted-foreground">
            {timeRange(start, end)}
            {pick.estimated_minutes !== null && `（見積もり ${formatMinutes(pick.estimated_minutes)}）`}
          </p>
        </div>
        <p className="text-xs">{pick.reason}</p>
        {(pick.deadline || pick.project) && (
          <p className="text-[11px] text-muted-foreground">
            {pick.deadline && `締切 ${formatJstDate(pick.deadline)} ${formatJstTime(pick.deadline)}`}
            {pick.deadline && pick.project && '・'}
            {pick.project}
          </p>
        )}
        <div className="flex gap-2">
          <Button size="sm" disabled={past || saving} onClick={() => void handleSchedule()} className="flex-1">
            {saving ? <Loader2 className="animate-spin" /> : <CalendarPlus />}
            {past ? '時間が過ぎています' : 'この時間に入れる'}
          </Button>
          {todo && onTodoClick && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setOpen(false)
                onTodoClick(todo)
              }}
            >
              開く
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

type LayerProps = {
  overlay: TodayOverlay
  hourHeight: number
  todos: TodoResponse[]
  onTodoClick?: (todo: TodoResponse) => void
  /** タスクと提案を合わせた重なりレイアウト（提案は overlay.picks の id で引く） */
  layout: Map<string, OverlapSlot>
  /** TODO 列右端の集中・運動の帯の幅（帯が無ければ 0） */
  laneWidth: number
}

/** タイムグリッド（position: relative）の子として置く */
export const TodaySuggestionLayer = ({ overlay, hourHeight, todos, onTodoClick, layout, laneWidth }: LayerProps) => {
  const now = useNow()
  const geometry = { hourHeight }
  return (
    <>
      {overlay.bands.map((band) => {
        const style = ENERGY_STYLE.get(band.energy)
        return (
          <div
            key={band.id}
            title={`${band.label}（${style?.label ?? band.energy}）`}
            className={cn('absolute z-6 rounded-full opacity-80', style?.className)}
            style={{
              ...verticalStyle(band, geometry),
              left: TIME_AXIS_WIDTH - ENERGY_BAND_WIDTH - 1,
              width: ENERGY_BAND_WIDTH,
            }}
          />
        )
      })}
      {hasSlotLane(overlay) && (
        <div
          className="pointer-events-none absolute top-0 bottom-0 z-4 border-l border-dashed border-border bg-muted/30"
          style={{ left: LANE_LEFT, width: SLOT_LANE_WIDTH }}
        />
      )}
      {[...overlay.focus, ...overlay.exercise].map((item) => (
        <SlotItem key={item.id} item={item} geometry={geometry} />
      ))}
      {overlay.picks.map((item) => (
        <PickItem
          key={item.id}
          item={item}
          geometry={geometry}
          now={now}
          todos={todos}
          onTodoClick={onTodoClick}
          layout={layout}
          laneWidth={laneWidth}
        />
      ))}
    </>
  )
}

type RowProps = { today: TodayAdviceResponse | null; generating: boolean }

/** タイムグリッドの上に出す、今日の調子・集中できる時間・運動の時間のまとめと、過ごし方の設定への入口 */
export const TodayAdviceRow = ({ today, generating }: RowProps) => {
  return (
    <div className="grid grid-cols-[48px_1fr] border-b bg-background">
      <div className="flex items-center justify-end pr-2">
        <span className="text-[0.65rem] text-muted-foreground">調子</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-1.5 text-xs">
        {today && (
          <span
            className={cn('rounded-full px-2 py-0.5 font-medium', CONDITION_STYLE.get(today.condition.level))}
            title={today.condition.reasons.join('\n') || undefined}
          >
            {today.condition.label}
          </span>
        )}
        {today?.focus && <span className="text-muted-foreground">{today.focus.summary}</span>}
        {today?.exercise_slots?.map((slot) => {
          const style = SLOT_STYLE.get(slot.kind)
          const Icon = style?.icon ?? Dumbbell
          return (
            <span
              key={`${slot.kind}-${slot.start}`}
              className={cn('flex items-center gap-1 tabular-nums', style?.text)}
              title={slot.detail}
            >
              <Icon className="size-3" />
              {timeRange(slot.start, slot.end)} {slot.title}
            </span>
          )
        })}
        {today && (
          <span className="flex items-center gap-2 text-[11px] text-muted-foreground">
            {[...ENERGY_STYLE].map(([energy, style]) => (
              <span key={energy} className="flex items-center gap-1">
                <span className={cn('h-2.5 w-1 rounded-full', style.className)} />
                {style.label}
              </span>
            ))}
            <span className="flex items-center gap-1">
              <span className="h-2.5 w-3 rounded-sm border border-dashed border-indigo-500 bg-indigo-500/15" />
              提案
            </span>
          </span>
        )}
        {generating && (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Loader2 className="size-3 animate-spin" />
            今日の提案を作り直しています…
          </span>
        )}
        <TodaySettingsButton />
      </div>
    </div>
  )
}
